import { IGetRevisionHistory } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VisuallyHidden
} from '@chakra-ui/react';
import { Badge, ButtonIcon, ButtonText, MenuItem } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faFile,
  faInfoCircle,
  faPeopleArrows
} from '@fortawesome/pro-regular-svg-icons';
import { faUserTag } from '@fortawesome/pro-solid-svg-icons';
import {
  recommendationBadges,
  reviewStatusBadges
} from 'src/components/Review/Badges';
import { openFile } from 'src/utils/file';
import AdjudicationRoundHistory from './AdjudicationRoundHistory';
import { useState } from 'react';
import { ReviewerFeedbackType } from './ReviewsHistory';
import ReviewDetailsReadonly from './ReadOnly/ReviewDetailsReadonly';
import MultilineText from '../../../MultilineText';
import { getReviewerNameOrEmail } from '../../../../utils/reviews';
import {
  IInvitedReviewer,
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment,
  IReviewTimeline,
  IReviewTimelines,
  postPeerReviewStates
} from '@publishing-engine/domain/models';
import FileViewerButtonText from '../../FileViewerButtonText';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { renderScoreBadge } from '../TableColumns';

const getReviewerFeedbackInRound = (
  revision: IGetRevisionHistory,
  reviewId: string
) =>
  revision.reviewerRevisionFeedback?.find(
    feedback => feedback.reviewId === reviewId
  ) ??
  revision.reviewerOriginalFeedback?.find(
    feedback => feedback.reviewId === reviewId
  ) ??
  null;

export const renderReviewScoreColumn = (
  review:
    | IPeerReviewFirstAssessment
    | IPeerReviewRevisionAssessment
    | null
    | undefined,
  reviewState: string
) => {
  const { reviewScore } = review || {};

  if (reviewScore?.score) {
    return renderScoreBadge(reviewScore.score, reviewScore.justification);
  }

  switch (reviewState) {
    case 'invited':
    case 'declined':
    case 'expired':
    case 'withdrawn':
    case 'awaitingRevisionReviewRequest':
    case 'closed':
      return 'N/A';

    case 'rescinded':
    case 'firstAssessment':
    case 'revisionInvited':
    case 'revisionAssessment':
      return recommendationBadges.awaitingReview;

    default:
      return (
        <Badge variant="neutral" size="context">
          Awaiting
        </Badge>
      );
  }
};

const ReviewerRevisionOverviewContent = ({
  revision,
  revisionRound,
  reviewFieldConfig,
  onReinviteReviewer,
  onReassignBoardMember,
  onRateReview
}: {
  revision: IGetRevisionHistory & { timelines?: IReviewTimelines[] };
  revisionRound: number;
  reviewFieldConfig: any;
  onReinviteReviewer?: (reviewer: IInvitedReviewer) => void;
  onReassignBoardMember?: (reviewer: IInvitedReviewer) => void;
  onRateReview?: (reviewer: IInvitedReviewer, receiveFeedback?: string) => void;
}) => {
  const [showReviewDetails, setShowReviewDetails] = useState<boolean>(false);
  const [selectedReviewerFeedback, setSelectedReviewerFeedback] =
    useState<ReviewerFeedbackType | null>(null);
  const [selectedRoundForReviewDetails, setSelectedRoundForReviewDetails] =
    useState<number>(0);
  const [reviewTimeline, setReviewTimeline] = useState<IReviewTimeline | null>(
    null
  );
  const { submissionId, workflowResponse } = useSubmissionData();
  const currentSubmissionState = workflowResponse.workflowData.currentState;
  /**
   * Reviewers can no longer be invited once a round is being finalized
   * (`revisionAssessment`) nor once the submission has left peer review: it
   * enters pre-production when the editors have decided on the last round, and
   * every phase after that inherits it, so re-invite and re-assign are disabled
   * from pre-production onwards too.
   */
  const isReinviteDisabledPhase =
    currentSubmissionState === 'revisionAssessment' ||
    postPeerReviewStates.includes(currentSubmissionState);

  /**
   * The review-workflow actions available in a row's current state. The row
   * carries the *review's* state rather than a review object, so this stands in
   * for `availableReviewActions.contains(review, 'RateReview')` in the review
   * detail view — that is the list the server validates the command against, so
   * an action is never offered for a review the API would reject.
   */
  const getReviewStateActions = (invitedReviewer?: IInvitedReviewer) => {
    const reviewState = invitedReviewer?.workflowCurrentState ?? '';

    return (
      workflowResponse.articleWorkflow.transitionGraph.review.states[
        reviewState
      ]?.actions ?? []
    );
  };

  const selectedReviewerFeedbackLive =
    selectedReviewerFeedback === null
      ? null
      : revision.reviewerRevisionFeedback?.find(
          feedback => feedback.reviewId === selectedReviewerFeedback.reviewId
        ) ?? selectedReviewerFeedback;

  const selectedReviewAssessment =
    selectedReviewerFeedbackLive?.reviewAssessment ?? null;
  const selectedInvitedReviewer = revision.invitedReviewers?.find(
    invitedReviewer =>
      invitedReviewer.reviewId === selectedReviewerFeedback?.reviewId
  );
  // The readonly view is rendered outside the table, so its Rate Review button
  // is gated on the same row the menu item is, looked up by the selected review.
  const canRateSelectedReview =
    !!onRateReview && !!selectedInvitedReviewer?.canRateReview;
  const selectedReceiveFeedback =
    selectedReviewAssessment && 'receiveFeedback' in selectedReviewAssessment
      ? selectedReviewAssessment.receiveFeedback
      : undefined;

  return (
    <>
      {showReviewDetails && selectedReviewerFeedback?.recommendation ? (
        <ReviewDetailsReadonly
          onClose={() => {
            setSelectedReviewerFeedback(null);
            setSelectedRoundForReviewDetails(0);
            setShowReviewDetails(false);
          }}
          roundIndex={selectedRoundForReviewDetails}
          reviewer={selectedReviewerFeedbackLive!.reviewer!}
          reviewAssessment={selectedReviewAssessment!}
          reviewFieldConfig={reviewFieldConfig}
          coReview={selectedReviewerFeedbackLive!.coReview}
          reviewTimeline={reviewTimeline!}
          canRateReview={canRateSelectedReview}
          onRateReview={() => {
            if (selectedInvitedReviewer) {
              onRateReview?.(selectedInvitedReviewer, selectedReceiveFeedback);
            }
          }}
        />
      ) : null}

      <Flex flexDirection="column" gap={2} mb={2}>
        <Text textStyle="headingGroup">Editor's Decision</Text>
        {revision.type === 'transfer' || revision.severity ? (
          <Flex>
            {
              recommendationBadges[
                revision.type === 'transfer'
                  ? 'rejectAndTransfer'
                  : `${revision.severity}Revision`
              ]
            }
          </Flex>
        ) : (
          revision.type === 'review' && (
            <Flex>{recommendationBadges['revision']}</Flex>
          )
        )}
        <MultilineText
          textStyle="textBody"
          text={revision?.editorialComment ?? 'No comment from editors.'}
        />
        <Flex flexDirection="column" gap={0}>
          {' '}
          {revision.editorialAttachments &&
            revision.editorialAttachments.map(file => (
              <Flex justifyContent="space-between" key={file?.referenceName}>
                <Flex gap={2} alignItems="center">
                  <FontAwesomeIcon icon={faFile} />
                  <Text textStyle="textBody">{file?.referenceName}</Text>
                  <FileViewerButtonText
                    file={file}
                    submissionContextId={submissionId}
                    textStyle="textBody"
                    flexShrink={0}
                  >
                    View
                  </FileViewerButtonText>
                </Flex>
              </Flex>
            ))}
        </Flex>
      </Flex>

      <Flex flexDirection="column" gap={6}>
        {revision?.authorResponse && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Author's Response</Text>
            <Text textStyle="headingItem">Comment</Text>
            <Text textStyle="textBody">{revision.authorResponse}</Text>
            {revision.authorResponseAttachments &&
              revision.authorResponseAttachments.length > 0 && (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">Attachment</Text>
                  <Flex flexDirection="column" gap={0}>
                    {' '}
                    {revision.authorResponseAttachments.map(file => (
                      <Flex
                        justifyContent="space-between"
                        key={file?.referenceName}
                      >
                        <Flex gap={2} alignItems="center">
                          <FontAwesomeIcon icon={faFile} />
                          <Text textStyle="textBody">
                            {file?.referenceName}
                          </Text>
                          <FileViewerButtonText
                            file={file}
                            submissionContextId={submissionId}
                            textStyle="textBody"
                            flexShrink={0}
                          >
                            View
                          </FileViewerButtonText>
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              )}
          </Flex>
        )}
        {revision?.manuscript && (
          <Flex flexDirection="column" gap={4}>
            <Text textStyle="headingGroup">Reviewed Manuscript</Text>
            <Flex justifyContent="space-between" key={revision.manuscript.id}>
              <Flex
                justifyContent="space-between"
                width="100%"
                padding={4}
                borderRadius="8px"
                border="1px"
                borderColor="neutralPrimaryBorder"
              >
                <Flex gap={2} alignItems="center">
                  <Flex>
                    <FontAwesomeIcon icon={faFile} fontSize={24} />
                  </Flex>
                  <Text textStyle="textBody">{revision.manuscript.name}</Text>
                </Flex>
                {revision?.manuscript?.url && (
                  <ButtonText
                    onClick={() => openFile(revision.manuscript?.url as string)}
                  >
                    View
                  </ButtonText>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        {revision?.reviewerRevisionFeedback?.length ||
        revision?.invitedReviewers?.length ? (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Round Reviews</Text>
            <Box overflowX="auto">
              <Box minWidth={{ base: '860px', md: '100%' }}>
                <Table size="sm" width="100%">
                  <Thead>
                    <Tr
                      sx={{
                        '& th': {
                          textTransform: 'none',
                          color: 'black',
                          background: 'white'
                        }
                      }}
                    >
                      <Th p={4} textAlign="left">
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Reviewer
                        </Text>
                      </Th>
                      <Th p={4} textAlign="left">
                        <Flex alignItems="center" gap={2}>
                          <Text textStyle="headingItem" whiteSpace="nowrap">
                            Status
                          </Text>
                          <Tooltip
                            label={
                              'Review statuses at the time editors finalized this round.'
                            }
                            placement="top"
                          >
                            <Box>
                              <ButtonIcon
                                size="small"
                                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                                ariaLabel="status-info"
                              />
                            </Box>
                          </Tooltip>
                        </Flex>
                      </Th>
                      <Th p={4} textAlign="left">
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Recommendation/Score
                        </Text>
                      </Th>
                      <Th>
                        <VisuallyHidden>Actions</VisuallyHidden>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {revision?.invitedReviewers?.map(invitedReviewer => {
                      const isInActiveReview =
                        !!invitedReviewer.isInActiveReview;
                      const reviewerFeedback = getReviewerFeedbackInRound(
                        revision,
                        invitedReviewer.reviewId
                      );

                      const timelineItem = revision.timelines?.find(
                        timeline =>
                          timeline.reviewId === invitedReviewer.reviewId
                      );

                      const reviewAssessment =
                        reviewerFeedback?.reviewAssessment ?? null;
                      const reviewState =
                        invitedReviewer.workflowCurrentState ?? '';
                      const reviewStateActions =
                        getReviewStateActions(invitedReviewer);
                      // The states a fresh invitation can be sent from, matching
                      // the commands `resendInviteMutations` can run. A reviewer
                      // who has already been re-invited is not among them, so the
                      // item must not offer to invite them again.
                      const isReinvitable = reviewStateActions.some(
                        action =>
                          action.name === 'ResendDeclinedReviewInvitation' ||
                          action.name === 'ResendExpiredReviewInvitation' ||
                          action.name === 'RequestRevisionReview' ||
                          action.name.startsWith(
                            'ResendWithdrawnReviewInvitation'
                          )
                      );
                      const isReviewRated =
                        !!reviewAssessment?.reviewScore ||
                        !!invitedReviewer.isReviewRated;
                      const canRateReview =
                        !!onRateReview && !!invitedReviewer.canRateReview;
                      const receiveFeedback =
                        reviewAssessment &&
                        'receiveFeedback' in reviewAssessment
                          ? reviewAssessment.receiveFeedback
                          : undefined;

                      return (
                        <Tr
                          background="white"
                          key={
                            reviewerFeedback?.reviewId ??
                            invitedReviewer.reviewId
                          }
                        >
                          <Td textStyle="headingItem" textAlign="left">
                            <Flex
                              flexDirection="column"
                              alignItems="flex-start"
                              gap={2}
                            >
                              <Box maxWidth={'full'}>
                                <Flex alignItems="center" gap={2}>
                                  <Tooltip
                                    label={getReviewerNameOrEmail(
                                      invitedReviewer
                                    )}
                                    placement="top"
                                    hasArrow
                                  >
                                    <Text textStyle="headingLine" isTruncated>
                                      {getReviewerNameOrEmail(invitedReviewer)}
                                    </Text>
                                  </Tooltip>
                                  {invitedReviewer.isBoardMember && (
                                    <Tooltip
                                      placement="top"
                                      label="Board Member"
                                      shouldWrapChildren
                                    >
                                      <Badge
                                        variant="neutral"
                                        cursor="pointer"
                                        size="context"
                                      >
                                        <FontAwesomeIcon
                                          size="sm"
                                          icon={faUserTag}
                                        />
                                      </Badge>
                                    </Tooltip>
                                  )}
                                </Flex>
                                <Text textStyle="textContext">
                                  {reviewerFeedback?.reviewer
                                    ?.affiliationDetails?.organizationName ||
                                    invitedReviewer?.affiliation}
                                </Text>
                              </Box>

                              {reviewerFeedback?.coReview?.status ===
                                'accepted' && (
                                <>
                                  <Box maxWidth={'full'} paddingX={2}>
                                    <Text textStyle="headingLine">
                                      {
                                        reviewerFeedback?.coReview.coReviewer
                                          .firstName
                                      }{' '}
                                      {
                                        reviewerFeedback?.coReview.coReviewer
                                          .lastName
                                      }
                                    </Text>
                                    {reviewerFeedback?.coReview.coReviewer
                                      .affiliation && (
                                      <Text textStyle="textContext">
                                        {
                                          reviewerFeedback?.coReview.coReviewer
                                            .affiliation
                                        }
                                      </Text>
                                    )}
                                    <Badge variant="attention" size="context">
                                      <Flex gap={1} alignItems="center">
                                        <FontAwesomeIcon
                                          icon={faPeopleArrows}
                                        />
                                        Co-Reviewer
                                      </Flex>
                                    </Badge>
                                  </Box>
                                </>
                              )}
                            </Flex>
                          </Td>
                          <Td textAlign="left">
                            {reviewStatusBadges[invitedReviewer.status]}
                          </Td>
                          <Td textAlign="left">
                            <Flex direction="column" gap={2}>
                              <Box>
                                {recommendationBadges[
                                  reviewerFeedback?.recommendation as string
                                ] ?? 'N/A'}
                              </Box>

                              {/* Score */}
                              <Flex align="center" gap={1}>
                                <Text textStyle="textContext">Score:</Text>
                                {renderReviewScoreColumn(
                                  reviewerFeedback?.reviewAssessment,
                                  reviewState
                                )}
                              </Flex>
                            </Flex>
                          </Td>
                          <Td textAlign="right">
                            <Flex justifyContent="flex-end">
                              <Menu aria-label="reviewer actions menu">
                                <MenuButton>
                                  <ButtonText>
                                    <Flex gap={2} alignItems="center">
                                      <FontAwesomeIcon
                                        size="lg"
                                        icon={faBars}
                                      />
                                    </Flex>
                                  </ButtonText>
                                </MenuButton>
                                <MenuList>
                                  {!isInActiveReview && (
                                    <MenuItem
                                      ariaLabel={
                                        invitedReviewer.isBoardMember
                                          ? 'Re-Assign'
                                          : 'Re-Invite'
                                      }
                                      disabled={
                                        isReinviteDisabledPhase ||
                                        (!invitedReviewer.isBoardMember &&
                                          !isReinvitable)
                                      }
                                      onClick={
                                        invitedReviewer.isBoardMember
                                          ? () =>
                                              onReassignBoardMember?.(
                                                invitedReviewer
                                              )
                                          : () =>
                                              onReinviteReviewer?.(
                                                invitedReviewer
                                              )
                                      }
                                    >
                                      {invitedReviewer.isBoardMember
                                        ? 'Re-Assign'
                                        : 'Re-Invite'}
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    ariaLabel="View Review"
                                    disabled={
                                      !reviewerFeedback?.reviewAssessment
                                    }
                                    onClick={() => {
                                      setSelectedReviewerFeedback(
                                        reviewerFeedback
                                      );
                                      setSelectedRoundForReviewDetails(
                                        revisionRound
                                      );
                                      setReviewTimeline(timelineItem!.timeline);
                                      setShowReviewDetails(true);
                                    }}
                                  >
                                    View Review
                                  </MenuItem>
                                  {canRateReview && (
                                    <MenuItem
                                      ariaLabel="Rate Review"
                                      disabled={isReviewRated}
                                      onClick={() =>
                                        onRateReview?.(
                                          invitedReviewer,
                                          receiveFeedback
                                        )
                                      }
                                    >
                                      Rate Review
                                    </MenuItem>
                                  )}
                                </MenuList>
                              </Menu>
                            </Flex>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </Flex>
        ) : null}

        {revision?.adjudicatorOriginalFeedback?.length ? (
          <AdjudicationRoundHistory
            revision={revision}
            configs={reviewFieldConfig}
            roundIndex={revisionRound}
          />
        ) : null}
      </Flex>
    </>
  );
};

export default ReviewerRevisionOverviewContent;
